import axios from 'axios';

import { KanzhongBackend } from 'constant/KanzhongBackend';
import { ServerError, createApiError } from 'stores/ApiClientError';

// --- 型定義 ---

export type CreateClientParams = {
  clientName: string;
  clientNameKana: string;
  clientTypeId: string;
  corporateNumber?: string;
  invoiceNumber?: string;
  address?: string;
  url?: string;
  contactName?: string;
  phoneNumber?: string;
  mailAddress?: string;
  contractStartDate: string;
  remarks?: string;
};

export type UpdateClientParams = {
  clientId: string;
  clientName: string;
  clientNameKana: string;
  clientTypeId: string;
  corporateNumber?: string;
  invoiceNumber?: string;
  address?: string;
  url?: string;
  contactName?: string;
  phoneNumber?: string;
  mailAddress?: string;
  contractStartDate: string;
  contractEndDate?: string;
  remarks?: string;
};

export type GetClientParams = {
  clientId: string;
};

export type ListClientParams = {
  clientName?: string;
  clientNameKana?: string;
  clientTypeId?: string;
  includeExpired?: boolean;
};

export type ClientResult = {
  clientId: string;
  clientName: string;
  clientNameKana: string;
  clientTypeId: string;
  corporateNumber?: string;
  invoiceNumber?: string;
  address?: string;
  url?: string;
  contactName?: string;
  phoneNumber?: string;
  mailAddress?: string;
  contractStartDate: string;
  contractEndDate?: string;
  remarks?: string;
  createdAt: string;
  createdBy: string;
  createdByName: string;
};

// --- API関数 ---

/**
 * 取引先登録API
 * @param params.clientName - 取引先名（必須, 最大300文字）
 * @param params.clientNameKana - 取引先名カナ（必須, 最大300文字）
 * @param params.clientTypeId - 取引先区分ID（必須, 最大300文字）
 * @param params.corporateNumber - 法人番号（任意, 最大20文字）
 * @param params.invoiceNumber - 登録番号/インボイス番号 T+13桁（任意, 最大20文字）
 * @param params.address - 住所（任意, 最大500文字）
 * @param params.url - 企業ホームページ（任意, 最大500文字）
 * @param params.contactName - 担当者名（任意, 最大300文字）
 * @param params.phoneNumber - 電話番号（任意, 最大20文字）
 * @param params.mailAddress - メールアドレス（任意, 最大300文字）
 * @param params.contractStartDate - 取引開始日（必須, format: yyyy-MM-dd'T'HH:mm:ss）
 * @param params.remarks - 備考（任意, 最大10000文字）
 */
export const createClient = async (params: CreateClientParams): Promise<string> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    const response = await axios.post(KanzhongBackend.CREATE_CLIENT, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });

    return response.data.clientId;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 取引先更新API
 * @param params.clientId - 取引先ID（必須, 最大64文字）
 * @param params.clientName - 取引先名（必須, 最大300文字）
 * @param params.clientNameKana - 取引先名カナ（必須, 最大300文字）
 * @param params.clientTypeId - 取引先区分ID（必須, 最大300文字）
 * @param params.corporateNumber - 法人番号（任意, 最大20文字）
 * @param params.invoiceNumber - 登録番号/インボイス番号 T+13桁（任意, 最大20文字）
 * @param params.address - 住所（任意, 最大500文字）
 * @param params.url - 企業ホームページ（任意, 最大500文字）
 * @param params.contactName - 担当者名（任意, 最大300文字）
 * @param params.phoneNumber - 電話番号（任意, 最大20文字）
 * @param params.mailAddress - メールアドレス（任意, 最大300文字）
 * @param params.contractStartDate - 取引開始日（必須, format: yyyy-MM-dd'T'HH:mm:ss）
 * @param params.contractEndDate - 取引終了日（任意, format: yyyy-MM-dd'T'HH:mm:ss）
 * @param params.remarks - 備考（任意, 最大10000文字）
 */
export const updateClient = async (params: UpdateClientParams): Promise<void> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    await axios.post(KanzhongBackend.UPDATE_CLIENT, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 取引先取得API
 * @param params.clientId - 取引先ID（必須, 最大64文字）
 */
export const getClient = async (params: GetClientParams): Promise<ClientResult> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    const response = await axios.post(KanzhongBackend.GET_CLIENT, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });

    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 取引先一覧取得API
 * @param params.clientName - 取引先名（任意, 前方一致）
 * @param params.clientNameKana - 取引先名カナ（任意, 前方一致）
 * @param params.clientTypeId - 取引先区分ID（任意, 完全一致）
 * @param params.includeExpired - 取引終了を含む（任意, boolean）
 */
export const listClient = async (params: ListClientParams): Promise<ClientResult[]> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    const response = await axios.post(KanzhongBackend.LIST_CLIENT, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });

    return response.data.results;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};
